import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Mail, X, Loader2, Send, Paperclip } from 'lucide-react';

/** Email the Final Compliance Package to compliance officer(s). */
const EmailFinalPackageModal = ({ apiUrl, folderId, finalPkg, onClose }) => {
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [ccMe, setCcMe] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${apiUrl}/api/settings`).then(r => r.ok ? r.json() : null).then(s => {
      if (s?.compliance_officer_email) setTo(s.compliance_officer_email);
    }).catch(() => {});
  }, [apiUrl]);

  const send = async () => {
    setError('');
    const recipients = to.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);
    if (recipients.length === 0) { setError('Enter at least one recipient email.'); return; }
    setSending(true);
    try {
      const r = await fetch(`${apiUrl}/api/client-files/${folderId}/final-package/email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: recipients, message, cc_me: ccMe }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(typeof d.detail === 'string' ? d.detail : 'Could not send the email.'); return; }
      setResult(d);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="email-final-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-[#0B3D7A] to-[#0077B6] text-white flex items-center justify-between">
          <div className="flex items-center gap-2"><Mail className="h-5 w-5" /><h2 className="font-bold">Email Final Compliance Package</h2></div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="email-final-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm bg-gray-50 border rounded-md px-3 py-2 text-gray-700">
            <Paperclip className="h-4 w-4 text-gray-500" /><span className="truncate">{finalPkg?.filename}</span>
            {finalPkg?.page_count ? <span className="text-xs text-gray-500 ml-auto whitespace-nowrap">{finalPkg.page_count} pages</span> : null}
          </div>
          {result ? (
            <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800" data-testid="email-final-success">
              Sent to {result.sent.join(', ')}.{result.failed?.length ? ` Failed: ${result.failed.join(', ')}.` : ''}
            </div>
          ) : (
            <>
              {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="email-final-error">{error}</div>}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">To (compliance officer) — comma-separated</label>
                <input type="text" value={to} onChange={(e) => setTo(e.target.value)} placeholder="compliance@utmostfinancial.ca"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]" data-testid="email-final-to" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Message (optional)</label>
                <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Notes for the compliance officer…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]" data-testid="email-final-message" />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={ccMe} onChange={(e) => setCcMe(e.target.checked)} className="h-4 w-4" data-testid="email-final-ccme" />
                Send me a copy
              </label>
            </>
          )}
        </div>
        <div className="px-5 py-3 bg-gray-50 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && (
            <Button onClick={send} disabled={sending} className="bg-[#0077B6] hover:bg-[#005A8C]" data-testid="email-final-send">
              {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}Send
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailFinalPackageModal;
